import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Close,
  Send,
  Stop,
  DeleteOutline,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { useChat } from '../contexts/ChatContext';

function ChatPanel() {
  const {
    messages,
    isOpen,
    isLoading,
    closeChat,
    sendMessage,
    clearMessages,
    stopGeneration,
  } = useChat();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <Paper
      elevation={4}
      sx={{
        width: 400,
        minWidth: 400,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ChatBubbleOutline fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            AI-avustaja
          </Typography>
        </Box>
        <Box>
          <Tooltip title="Tyhjennä keskustelu">
            <IconButton size="small" color="inherit" onClick={clearMessages}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sulje">
            <IconButton size="small" color="inherit" onClick={closeChat}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
              opacity: 0.6,
              textAlign: 'center',
              px: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              AI-avustaja
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Kysy mitä tahansa kirjanpidosta, verotuksesta tai taloushallinnosta.
            </Typography>
          </Box>
        )}

        {messages.map((msg, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <Box
              sx={{
                maxWidth: '85%',
                px: 2,
                py: 1,
                borderRadius: 2,
                bgcolor: msg.role === 'user' ? 'primary.main' : 'action.hover',
                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.875rem',
                lineHeight: 1.5,
              }}
            >
              {msg.content || (isLoading && index === messages.length - 1 ? '...' : '')}
            </Box>
          </Box>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Kirjoita viesti..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
        {isLoading ? (
          <Tooltip title="Pysäytä">
            <IconButton color="error" onClick={stopGeneration} size="small">
              <Stop />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Lähetä">
            <span>
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!input.trim()}
                size="small"
              >
                <Send />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}

export default ChatPanel;
